import { supabase } from '../lib/supabase';

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

/**
 * Get all audit logs with optional filtering
 * @param {Object} filters - Filter options
 * @param {string} filters.actionType - Filter by action type
 * @param {string} filters.entityType - Filter by entity type
 * @param {string} filters.userId - Filter by user ID
 * @param {string} filters.searchTerm - Search in description, user name, entity name
 * @param {Date} filters.startDate - Filter by start date
 * @param {Date} filters.endDate - Filter by end date
 * @param {number} filters.limit - Limit number of results
 * @returns {Promise<Array>} Array of audit logs
 */
export const getAuditLogs = async (filters = {}) => {
  try {
    let query = supabase?.from('audit_logs')?.select('*');

    // Apply filters
    if (filters?.actionType && filters?.actionType !== 'all') {
      query = query?.eq('action_type', filters?.actionType);
    }

    if (filters?.entityType && filters?.entityType !== 'all') {
      query = query?.eq('entity_type', filters?.entityType);
    }

    if (filters?.userId) {
      query = query?.eq('user_id', filters?.userId);
    }

    if (filters?.searchTerm) {
      query = query?.or(
        `description.ilike.%${filters?.searchTerm}%,user_name.ilike.%${filters?.searchTerm}%,user_email.ilike.%${filters?.searchTerm}%,entity_name.ilike.%${filters?.searchTerm}%`
      );
    }

    if (filters?.startDate) {
      query = query?.gte('timestamp', filters?.startDate?.toISOString());
    }

    if (filters?.endDate) {
      query = query?.lte('timestamp', filters?.endDate?.toISOString());
    }

    // Order by timestamp descending (most recent first)
    query = query?.order('timestamp', { ascending: false });

    // Apply limit
    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error fetching audit logs:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return [];
      }
      console.log('Error fetching audit logs:', error?.message);
      return [];
    }

    return toCamelCase(data || []);
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    console.log('Error fetching audit logs:', error?.message);
    return [];
  }
};

/**
 * Log a user action to audit logs
 * @param {Object} logData - Audit log data
 * @param {string} logData.actionType - Type of action (create, update, delete, etc.)
 * @param {string} logData.entityType - Type of entity (user, screen, feature, etc.)
 * @param {string} logData.entityId - ID of the entity
 * @param {string} logData.entityName - Name of the entity
 * @param {string} logData.description - Description of the action
 * @param {Object} logData.changesBefore - State before change (optional)
 * @param {Object} logData.changesAfter - State after change (optional)
 * @returns {Promise<Object>} Created audit log
 */
export const logAction = async (logData) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase?.auth?.getUser();
    
    if (userError || !user) {
      console.log('No authenticated user for audit log');
      return null;
    }

    // Get user profile for additional info
    const { data: profile } = await supabase?.from('user_profiles')?.select('email, full_name')?.eq('id', user?.id)?.maybeSingle();

    const auditData = {
      user_id: user?.id,
      user_email: profile?.email || user?.email,
      user_name: profile?.full_name || 'Unknown User',
      action_type: logData?.actionType,
      entity_type: logData?.entityType,
      entity_id: logData?.entityId || null,
      entity_name: logData?.entityName || null,
      description: logData?.description,
      changes_before: logData?.changesBefore || null,
      changes_after: logData?.changesAfter || null,
      ip_address: null, // Can be populated from request headers if available
      user_agent: navigator?.userAgent || null,
      timestamp: new Date()?.toISOString()
    };

    const { data, error } = await supabase?.from('audit_logs')?.insert(auditData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error creating audit log:', error?.message);
        throw error;
      }
      console.log('Error creating audit log:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    console.log('Error creating audit log:', error?.message);
    return null;
  }
};

/**
 * Get audit log statistics
 * @returns {Promise<Object>} Statistics object
 */
export const getAuditLogStats = async () => {
  try {
    const { data, error } = await supabase?.from('audit_logs')?.select('action_type, entity_type');

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error fetching audit log stats:', error?.message);
        throw error;
      }
      return { totalLogs: 0, byAction: {}, byEntity: {} };
    }

    const stats = {
      totalLogs: data?.length || 0,
      byAction: {},
      byEntity: {}
    };

    data?.forEach(log => {
      // Count by action type
      stats.byAction[log?.action_type] = (stats?.byAction?.[log?.action_type] || 0) + 1;
      // Count by entity type
      stats.byEntity[log?.entity_type] = (stats?.byEntity?.[log?.entity_type] || 0) + 1;
    });

    return toCamelCase(stats);
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    console.log('Error fetching audit log stats:', error?.message);
    return { totalLogs: 0, byAction: {}, byEntity: {} };
  }
};
